import clsx from 'clsx'
import type { MouseEventHandler, PropsWithChildren } from 'react'
import React from 'react'
import { toDataSet } from '../utilities/toDataSet'

export interface LinkProps {
  /**
   * Custom data attributes to be added to the link element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * Whether the link opens in a new tab with rel="noreferrer"
   * @default false
   */
  external?: boolean
  /**
   * The URL that the link points to
   */
  href: string
  /**
   * The id attribute of the link element
   */
  id?: string
  /**
   * Callback fired when the link is activated
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>
  /**
   * The size of the link text
   * @default "default"
   */
  size?: LinkSize
}

type LinkSize = 'default' | 'small'

type LinkSizes = {
  [key in LinkSize]: string
}

const sizes: LinkSizes = {
  default: 'text-base leading-6',
  small: 'text-sm leading-5',
}

function Link({
  children,
  dataSet,
  external = false,
  href,
  id,
  onClick,
  size = 'default',
}: PropsWithChildren<LinkProps>): JSX.Element {
  return (
    // We actually ARE using rel: 'noreferrer' here, which is recommended, but our syntax
    // isn't being parsed by the linter so we'll ignore this line for now.
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      className={clsx(
        'font-sans font-normal no-underline text-primary-main hover:text-primary-dark focus:text-primary-dark focus:outline-none focus-visible:ring active:text-primary-darker',
        sizes[size],
      )}
      {...toDataSet(dataSet)}
      href={href}
      id={id}
      onClick={onClick}
      target={external ? '_blank' : '_self'}
      {...(external && { rel: 'noreferrer' })}
    >
      {children}
    </a>
  )
}

export default Link
